/**
 * De balk zodra er een groep woningen gekozen is: hoeveel, en wat ermee kan.
 *
 * Staat op twee plekken: boven de woningtabel (aangevinkt, MA-17) en op de kaart
 * (een getekend gebied, MA-22). Dezelfde handelingen, dus één component.
 *
 * Keuren gaat meteen; verwijderen vraagt eerst om een bevestiging, met het
 * verschil tussen de twee erbij - een verwijderde woning komt bij de volgende
 * ronde terug als hij nog te koop staat, een afgekeurde niet. Sinds MA-22 zijn
 * er twee soorten afkeuren: gewoon (vervalt als de advertentie terugkomt) en
 * definitief (blijft).
 */
import type { Beoordeling } from '@mamimap/shared';
import { useState } from 'react';
import { useBeoordelenBulk, useVerwijderWoningen } from '@/lib/api/queries';
import { woningenTekst } from '@/screens/datasets/selectie';
import { Bevestiging } from './Bevestiging';
import { Melding } from './Melding';

type ActiebalkProps = {
  selectie: ReadonlySet<string>;
  onWis: () => void;
  /** Na een geslaagde handeling; de selectie is dan meestal leeg te maken. */
  onKlaar: () => void;
  /** Waar de groep vandaan komt, voor in de telling: "in dit gebied". */
  herkomst?: string;
  /** Verwijderen hoort bij de tabel; op de kaart is afkeuren de handeling. */
  metVerwijderen?: boolean;
  /** Ruimte eromheen; de balk verdwijnt helemaal als er niets te tonen is. */
  className?: string;
  /** Boven op de kaart: dan krijgt ook de uitkomst een eigen vlak, anders staat hij los op de straten. */
  zwevend?: boolean;
};

export function Actiebalk({
  selectie,
  onWis,
  onKlaar,
  herkomst,
  metVerwijderen = true,
  className = '',
  zwevend = false,
}: ActiebalkProps) {
  const keuren = useBeoordelenBulk();
  const verwijderen = useVerwijderWoningen();
  const [bevestig, zetBevestig] = useState(false);
  const [uitkomst, zetUitkomst] = useState<string | null>(null);
  const ids = [...selectie];
  const bezig = keuren.isPending || verwijderen.isPending;

  function keur(beoordeling: Beoordeling, woord: string) {
    keuren.mutate(
      { ids, beoordeling },
      {
        onSuccess: (uit) => {
          zetUitkomst(`${woningenTekst(uit.aantal)} ${woord}`);
          onKlaar();
        },
      },
    );
  }

  return (
    /* Blijft staan als de selectie leeg is, zodat de uitkomst ("28 woningen
       afgekeurd") te lezen is nadat de selectie is opgeruimd. */
    <div className={`flex flex-col gap-2 empty:hidden ${className}`}>
      {selectie.size > 0 ? (
        <div className="paneel flex flex-wrap items-center gap-2 px-3 py-2">
          <span className="cijfers text-[12.5px] text-ink">
            {woningenTekst(selectie.size)} {herkomst ?? 'geselecteerd'}
          </span>
          <button
            type="button"
            className="knop-stil"
            onClick={() => keur('afgekeurd', 'afgekeurd')}
            disabled={bezig}
            title="Komt weer in beeld als de advertentie terugkomt"
          >
            Afkeuren
          </button>
          <button
            type="button"
            className="knop-stil"
            onClick={() => keur('definitief', 'definitief afgekeurd')}
            disabled={bezig}
            title="Blijft weg, ook als de advertentie terugkomt"
          >
            Definitief afkeuren
          </button>
          <button
            type="button"
            className="knop-stil"
            onClick={() => keur('kansje', 'als kansje gezet')}
            disabled={bezig}
          >
            Kansje
          </button>
          <button
            type="button"
            className="knop-stil"
            onClick={() => keur('nieuw', 'teruggezet naar nieuw')}
            disabled={bezig}
          >
            Oordeel wissen
          </button>
          {metVerwijderen ? (
            <button
              type="button"
              className="knop-stil"
              onClick={() => zetBevestig(true)}
              disabled={bezig || bevestig}
            >
              Verwijderen
            </button>
          ) : null}
          <button
            type="button"
            onClick={onWis}
            className="ml-auto text-[12px] text-muted underline-offset-2 hover:text-ink hover:underline"
          >
            Selectie wissen
          </button>
        </div>
      ) : null}

      {bevestig && selectie.size > 0 ? (
        <Bevestiging
          vraag={`${woningenTekst(selectie.size)} verwijderen? Dit is niet terug te draaien.`}
          uitleg="Een woning die nog te koop staat komt bij de volgende ronde van de extensie gewoon weer binnen. Wil je hem niet meer zien, keur hem dan definitief af; dat blijft staan."
          knop="Verwijderen"
          bezig={verwijderen.isPending}
          fout={verwijderen.error}
          onBevestig={() =>
            verwijderen.mutate(ids, {
              onSuccess: (uit) => {
                zetBevestig(false);
                zetUitkomst(`${woningenTekst(uit.aantal)} verwijderd`);
                onKlaar();
              },
            })
          }
          onAnnuleer={() => zetBevestig(false)}
        />
      ) : null}

      {keuren.error ? <Melding fout={keuren.error} /> : null}
      {/* Een nieuwe selectie maakt de vorige uitkomst oud nieuws. */}
      {uitkomst && selectie.size === 0 ? (
        <p
          className={
            zwevend
              ? 'paneel self-start px-3 py-1.5 text-[12px] text-ink-soft'
              : 'text-[12px] text-muted'
          }
        >
          {uitkomst}
        </p>
      ) : null}
    </div>
  );
}
